import { useState, useMemo, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Target, Trophy, Plus, Search, User, Users,
  TrendingUp, Calendar, ChevronRight, X,
  PiggyBank, ArrowUpCircle, ArrowDownCircle,
  Pencil, Trash2, Clock, Sparkles, Check,
  AlertTriangle, History, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { format, differenceInMonths, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { 
  subscribeToGoals, 
  addGoal, 
  updateGoal, 
  deleteGoal,
  addMovement,
  updateMovement,
  deleteMovement
} from "@/services/goalsService";
import { subscribeToConnections } from "@/services/connectionsService";

// Motivational quotes based on progress
const getMotivationalQuote = (progress) => {
  if (progress === 0) {
    return { text: "Todo mundo começa do zero. A diferença é quem continua.", emoji: "🚀" };
  } else if (progress < 10) {
    return { text: "Pelo menos você começou. Já é mais do que a maioria faz.", emoji: "🌱" };
  } else if (progress < 50) {
    return { text: "Metade do caminho é a parte mais chata. Continue assim mesmo.", emoji: "💪" };
  } else if (progress < 90) {
    return { text: "Tá quase lá! Não é hora de relaxar, hein?", emoji: "🔥" };
  } else if (progress < 100) {
    return { text: "Sério que você vai desistir agora? Falta tão pouco!", emoji: "⚡" };
  } else {
    return { text: "Parabéns! Você provou que consegue. Agora cria outra meta.", emoji: "🏆" };
  }
};

// Format currency
const formatCurrency = (value) => {
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Filter options
const filterOptions = [
  { id: "all", label: "Todos" },
  { id: "individual", label: "Individual" },
  { id: "shared", label: "Compartilhado" },
  { id: "completed", label: "Concluído" },
  { id: "in_progress", label: "Em Andamento" },
];

export default function Goals() {
  const { user } = useAuth();
  
  // State
  const [goals, setGoals] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Modal states
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showEditMovementModal, setShowEditMovementModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteMovementConfirm, setDeleteMovementConfirm] = useState(null);
  
  // Selected items
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingMovement, setEditingMovement] = useState(null);

  // Subscribe to goals from Firestore
useEffect(() => {
    if (!user) return;
    
    setIsLoading(true);
    const unsubscribeGoals = subscribeToGoals(user.uid, (data) => {
      setGoals(data);
      setIsLoading(false);
    });

    const unsubscribeConnections = subscribeToConnections(user.uid, (data) => {
      setParticipants([
        { id: "me", name: "Eu", avatar: "👤" },
        ...data.people.map(p => ({ 
          id: p.id, 
          name: p.name, 
          avatar: p.nickname?.charAt(0) || "👤" 
        }))
      ]);
    });

    return () => {
      unsubscribeGoals();
      unsubscribeConnections();
    };
  }, [user]);
  
  // Form states
  const [goalForm, setGoalForm] = useState({
    title: "",
    targetAmount: "",
    startDate: new Date().toISOString().split('T')[0],
    endDate: "",
    isShared: false,
    participants: ["me"],
  });
  
  const [movementForm, setMovementForm] = useState({
    type: "deposit",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    participantId: "me",
    note: "",
  });

  // Calculate stats
  const stats = useMemo(() => {
    const total = goals.length;
    const completed = goals.filter(g => g.status === "completed").length;
    const avgProgress = goals.length > 0 
      ? Math.round(goals.reduce((sum, g) => sum + (g.currentAmount / g.targetAmount) * 100, 0) / goals.length)
      : 0;
    return { total, completed, avgProgress };
  }, [goals]);

  // Filter goals
  const filteredGoals = useMemo(() => {
    let filtered = goals;
    
    // Search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter(g => 
        g.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Category filter
    switch (activeFilter) {
      case "individual":
        filtered = filtered.filter(g => !g.isShared);
        break;
      case "shared":
        filtered = filtered.filter(g => g.isShared);
        break;
      case "completed":
        filtered = filtered.filter(g => g.status === "completed");
        break;
      case "in_progress":
        filtered = filtered.filter(g => g.status === "in_progress");
        break;
      default:
        break;
    }
    
    return filtered;
  }, [goals, searchQuery, activeFilter]);

  // Calculate participant balance in a goal
  const getParticipantBalance = useCallback((goal, participantId) => {
    const deposits = goal.movements
      .filter(m => m.participantId === participantId && m.type === "deposit")
      .reduce((sum, m) => sum + m.amount, 0);
    const withdrawals = goal.movements
      .filter(m => m.participantId === participantId && m.type === "withdrawal")
      .reduce((sum, m) => sum + m.amount, 0);
    return deposits - withdrawals;
  }, []);

  // Calculate participant contribution percentage
  const getParticipantContribution = useCallback((goal, participantId) => {
    const balance = getParticipantBalance(goal, participantId);
    if (goal.currentAmount === 0) return 0;
    return Math.max(0, Math.round((balance / goal.currentAmount) * 100));
  }, [getParticipantBalance]);

  // Calculate monthly installment suggestion
  const getMonthlyInstallment = useCallback((goal) => {
    if (!goal.endDate) return null;
    const remaining = goal.targetAmount - goal.currentAmount;
    if (remaining <= 0) return null;
    const monthsLeft = differenceInMonths(parseISO(goal.endDate), new Date());
    if (monthsLeft <= 0) return remaining;
    return Math.ceil(remaining / monthsLeft);
  }, []);

  // Reset forms
  const resetForms = () => {
    setGoalForm({
      title: "",
      targetAmount: "",
      startDate: new Date().toISOString().split('T')[0],
      endDate: "",
      isShared: false,
      participants: ["me"],
    });
    setMovementForm({
      type: "deposit",
      amount: "",
      date: new Date().toISOString().split('T')[0],
      participantId: "me",
      note: "",
    });
    setEditingGoal(null);
    setEditingMovement(null);
  };

  // Open goal for editing
  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setGoalForm({
      title: goal.title,
      targetAmount: goal.targetAmount.toString(),
      startDate: goal.startDate,
      endDate: goal.endDate,
      isShared: goal.isShared,
      participants: goal.participants,
    });
    setShowDetailsModal(false);
    setShowGoalModal(true);
  };

  // Save goal (create or update)
  const handleSaveGoal = async () => {
    if (!goalForm.title.trim()) {
      toast.error("Dá um nome pra essa meta, vai!");
      return;
    }
    if (!goalForm.targetAmount || parseFloat(goalForm.targetAmount) <= 0) {
      toast.error("Meta sem valor? Tá de brincadeira?");
      return;
    }

    setIsSaving(true);
    const goalData = {
      title: goalForm.title,
      targetAmount: parseFloat(goalForm.targetAmount),
      startDate: goalForm.startDate,
      endDate: goalForm.endDate,
      isShared: goalForm.isShared,
      participants: goalForm.isShared ? goalForm.participants : ["me"],
    };

    try {
      if (editingGoal) {
        await updateGoal(editingGoal.id, goalData);
        toast.success("Meta atualizada! Agora é só correr atrás.");
      } else {
        await addGoal(user.uid, goalData);
        toast.success("Nova meta criada! O difícil agora é cumprir.");
      }
      setShowGoalModal(false);
      resetForms();
    } catch (error) {
      toast.error("Erro ao salvar. Tente novamente.");
    }
    setIsSaving(false);
  };

  // Delete goal
  const handleDeleteGoal = async () => {
    if (!deleteConfirm) return;
    
    setIsSaving(true);
    try {
      await deleteGoal(deleteConfirm.id);
      toast.success("Meta excluída. Esperamos que não seja desistência!");
      setShowDetailsModal(false);
      setSelectedGoal(null);
    } catch (error) {
      toast.error("Erro ao excluir. Tente novamente.");
    }
    setIsSaving(false);
    setDeleteConfirm(null);
  };

  // Open movement modal
  const handleOpenMovementModal = (goal) => {
    setSelectedGoal(goal);
    setMovementForm({
      type: "deposit",
      amount: "",
      date: new Date().toISOString().split('T')[0],
      participantId: goal.participants?.[0] || "me",
      note: "",
    });
    setShowMovementModal(true);
  };

  // Save movement
  const handleSaveMovement = async () => {
    if (!movementForm.amount || parseFloat(movementForm.amount) <= 0) {
      toast.error("Valor inválido. Não aceita troco em bala, hein!");
      return;
    }

    const amount = parseFloat(movementForm.amount);

    // Validate withdrawal
    if (movementForm.type === "withdrawal") {
      const participantBalance = getParticipantBalance(selectedGoal, movementForm.participantId);
      if (amount > participantBalance) {
        const participant = participants.find(p => p.id === movementForm.participantId);
        toast.error(`${participant?.name || "Participante"} só tem R$ ${formatCurrency(participantBalance)} de saldo nessa meta. Não dá pra sacar mais que isso!`);
        return;
      }
    }

    setIsSaving(true);
    const movement = {
      type: movementForm.type,
      amount: amount,
      date: movementForm.date,
      participantId: movementForm.participantId,
      note: movementForm.note,
    };

    try {
      const updatedGoal = await addMovement(selectedGoal.id, movement);
      setSelectedGoal(updatedGoal);
      
      const emoji = movementForm.type === "deposit" ? "💰" : "📤";
      toast.success(`${emoji} ${movementForm.type === "deposit" ? "Aporte" : "Retirada"} registrado!`);
      setShowMovementModal(false);
      resetForms();
    } catch (error) {
      toast.error("Erro ao salvar. Tente novamente.");
    }
    setIsSaving(false);
  };

  // Edit movement
  const handleEditMovement = (movement) => {
    setEditingMovement(movement);
    setMovementForm({
      type: movement.type,
      amount: movement.amount.toString(),
      date: movement.date,
      participantId: movement.participantId,
      note: movement.note,
    });
    setShowEditMovementModal(true);
  };

  // Update movement
  const handleUpdateMovement = async () => {
    if (!editingMovement || !selectedGoal) return;
    
    const amount = parseFloat(movementForm.amount);
    if (!amount || amount <= 0) {
      toast.error("Valor inválido!");
      return;
    }

    setIsSaving(true);
    try {
      const updatedGoal = await updateMovement(selectedGoal.id, editingMovement.id, {
        ...movementForm,
        amount
      });
      setSelectedGoal(updatedGoal);
      toast.success("Movimentação atualizada!");
      setShowEditMovementModal(false);
      resetForms();
    } catch (error) {
      toast.error("Erro ao atualizar. Tente novamente.");
    }
    setIsSaving(false);
  };

  // Delete movement
  const handleDeleteMovement = async () => {
    if (!deleteMovementConfirm || !selectedGoal) return;

    setIsSaving(true);
    try {
      const updatedGoal = await deleteMovement(selectedGoal.id, deleteMovementConfirm.id);
      setSelectedGoal(updatedGoal);
      toast.success("Movimentação excluída. O histórico foi atualizado.");
    } catch (error) {
      toast.error("Erro ao excluir. Tente novamente.");
    }
    setIsSaving(false);
    setDeleteMovementConfirm(null);
  };

  // Open goal details
  const handleOpenDetails = (goal) => {
    setSelectedGoal(goal);
    setShowDetailsModal(true);
  };

  return (
    <div className="min-h-screen bg-[#fafbfc] pb-32">
      <div className="max-w-lg mx-auto px-5 py-6">
        
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-xl font-black tracking-tight text-slate-900 leading-none mb-1">
              Minhas Metas
            </h1>
          <p className="text-slate-500 text-sm">
            Criar metas é o primeiro passo para realizar sonhos. Vamos lá?
          </p>
        </motion.header>

        {/* Stats Grid Otimizado - Resiliência para Mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="grid grid-cols-3 gap-2 mb-6 p-1 rounded-[24px]"
        >
          {/* Total */}
          <div className="bg-white rounded-2xl px-1 py-5 border border-slate-200 shadow-sm text-center min-w-0 flex flex-col items-center justify-center">
            <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center mb-2 shadow-sm">
              <Target className="w-5 h-5 text-indigo-500" />
            </div>
            <p className="text-[10px] text-slate-400 uppercase font-black tracking-tighter mb-1 truncate w-full">Total</p>
            <p className="text-[18px] font-black text-slate-900 truncate w-full px-1">
              {stats.total}
            </p>
          </div>

          {/* Concluídas */}
          <div className="bg-white rounded-2xl px-1 py-5 border border-slate-200 shadow-sm text-center min-w-0 flex flex-col items-center justify-center">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center mb-2 shadow-sm">
              <Trophy className="w-5 h-5 text-emerald-500" />
            </div>
            <p className="text-[10px] text-slate-400 uppercase font-black tracking-tighter mb-1 truncate w-full">Concluídas</p>
            <p className="text-[18px] font-black text-emerald-600 truncate w-full px-1">
              {stats.completed}
            </p>
          </div>

          {/* Progresso */}
          <div className="bg-white rounded-2xl px-1 py-5 border border-slate-200 shadow-sm text-center min-w-0 flex flex-col items-center justify-center">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center mb-2 shadow-sm">
              <TrendingUp className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-[10px] text-slate-400 uppercase font-black tracking-tighter mb-1 truncate w-full">Progresso</p>
            <p className="text-[18px] font-black text-amber-600 truncate w-full px-1">
              {stats.avgProgress}%
            </p>
          </div>
        </motion.div>

        {/* Search & Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-3 mb-6"
        >
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Buscar metas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-xl bg-white border-slate-200"
              data-testid="search-goals"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {filterOptions.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                data-testid={`filter-${filter.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeFilter === filter.id
                    ? "bg-indigo-500 text-white shadow-md shadow-indigo-500/30"
                    : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Goals List */}
        {filteredGoals.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Target className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="font-bold text-slate-700 mb-2">
              {searchQuery ? "Nenhuma meta encontrada" : "Nenhuma meta ainda"}
            </h3>
            <p className="text-slate-500 text-sm">
              {searchQuery 
                ? "Tente buscar por outro termo" 
                : "Que tal criar sua primeira meta?"}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {filteredGoals.map((goal, index) => {
                const progress = Math.min(100, Math.round((goal.currentAmount / goal.targetAmount) * 100));
                const isCompleted = progress >= 100;
                const quote = getMotivationalQuote(progress);
                const monthlyInstallment = getMonthlyInstallment(goal);

                return (
                  <motion.div
                    key={goal.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleOpenDetails(goal)}
                    className="bg-white rounded-[24px] p-5 border border-slate-200 cursor-pointer hover:shadow-lg hover:border-slate-300 transition-all"
                    data-testid={`goal-card-${goal.id}`}
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                        isCompleted 
                          ? "bg-emerald-100" 
                          : "bg-indigo-100"
                      }`}>
                        {isCompleted 
                          ? <Trophy className="w-6 h-6 text-emerald-600" />
                          : <Target className="w-6 h-6 text-indigo-600" />
                        }
                      </div>
                      <span className={`text-xl font-black ${
                        isCompleted ? "text-emerald-600" : "text-indigo-600"
                      }`}>
                        {progress}%
                      </span>
                    </div>

                    {/* Title & Badge */}
                    <div className="mb-4">
                      <h3 className="font-bold text-slate-900 text-lg mb-1">{goal.title}</h3>
                      <Badge 
                        variant="secondary" 
                        className={`text-xs ${
                          goal.isShared 
                            ? "bg-violet-100 text-violet-700" 
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {goal.isShared ? (
                          <><Users className="w-3 h-3 mr-1" /> Compartilhada</>
                        ) : (
                          <><User className="w-3 h-3 mr-1" /> Individual</>
                        )}
                      </Badge>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <Progress 
                        value={progress} 
                        className={`h-2 ${isCompleted ? "[&>div]:bg-emerald-500" : "[&>div]:bg-indigo-500"}`}
                      />
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <span className="font-bold text-slate-900">
                          R$ {formatCurrency(goal.currentAmount)}
                        </span>
                        <span className="text-slate-400"> / R$ {formatCurrency(goal.targetAmount)}</span>
                      </div>
                      {goal.endDate && (
                        <div className="flex items-center gap-1 text-slate-500">
                          <Calendar className="w-4 h-4" />
                          <span>{format(parseISO(goal.endDate), "MMM yyyy", { locale: ptBR })}</span>
                        </div>
                      )}
                    </div>

                    {/* Installment suggestion */}
                    {monthlyInstallment && !isCompleted && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
                        <Sparkles className="w-3 h-3 text-amber-500" />
                        <span>Parcela sugerida: <strong className="text-slate-700">R$ {formatCurrency(monthlyInstallment)}/mês</strong></span>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* FAB */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.3, type: "spring" }}
        className="fixed bottom-28 right-6 z-40"
      >
        <Button
          onClick={() => { resetForms(); setShowGoalModal(true); }}
          className="w-14 h-14 rounded-full bg-indigo-500 hover:bg-indigo-600 shadow-xl shadow-indigo-500/30"
          data-testid="add-goal-fab"
        >
          <Plus className="w-6 h-6" />
        </Button>
      </motion.div>

      {/* Create/Edit Goal Modal */}
      <Dialog open={showGoalModal} onOpenChange={(open) => { setShowGoalModal(open); if (!open) resetForms(); }}>
        <DialogContent className="w-full max-w-full h-[100dvh] max-h-[100dvh] sm:max-w-md sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-[24px] border-0 sm:border overflow-y-auto p-6">
          <DialogHeader>
  <DialogTitle className="text-xl font-bold">
    {editingGoal ? "Editar Meta" : "Nova Meta"}
  </DialogTitle>
</DialogHeader>

{/* Novo Card do Oráculo para preencher o espaço e educar */}
{!editingGoal && (
  <div className="mt-4 bg-indigo-50 rounded-[20px] p-4 border border-indigo-100 flex gap-3 items-start animate-in fade-in zoom-in-95">
    <div className="w-10 h-10 rounded-xl bg-indigo-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-indigo-200">
      <Sparkles className="w-5 h-5 text-white" />
    </div>
    <div className="space-y-1">
      <p className="text-[12px] font-black text-indigo-400 uppercase tracking-wider">Dica do Oráculo</p>
      <p className="text-xs text-indigo-900 leading-tight font-medium">
        "Uma meta sem um plano é apenas um desejo. Seja específico no nome e realista no valor, ou o Oráculo vai rir de você depois." 🔮
      </p>
    </div>
  </div>
)}

<div className="space-y-4 pt-4">
  <div className="space-y-2">
    <Label>Título da Meta *</Label>
              <Input
                placeholder="Ex: Viagem dos sonhos, Carro novo..."
                value={goalForm.title}
                onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                className="rounded-xl h-12"
                data-testid="goal-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Valor Alvo *</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">R$</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={goalForm.targetAmount ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(goalForm.targetAmount) : ""}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "") / 100;
                    setGoalForm({ ...goalForm, targetAmount: val.toString() });
                  }}
                  className="rounded-xl h-12 pl-12 font-bold"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={goalForm.startDate}
                  onChange={(e) => setGoalForm({ ...goalForm, startDate: e.target.value })}
                  className="rounded-xl h-12"
                  data-testid="goal-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={goalForm.endDate}
                  onChange={(e) => setGoalForm({ ...goalForm, endDate: e.target.value })}
                  className="rounded-xl h-12"
                  data-testid="goal-end-date"
                />
              </div>
            </div>

            {/* Monthly installment preview */}
            {goalForm.targetAmount && goalForm.endDate && (
              <div className="bg-amber-50 rounded-xl p-3 flex items-center gap-2 text-sm">
                <PiggyBank className="w-5 h-5 text-amber-600" />
                <span className="text-amber-800">
                  Parcela mensal sugerida: <strong>R$ {formatCurrency(
                    Math.ceil(parseFloat(goalForm.targetAmount) / Math.max(1, differenceInMonths(parseISO(goalForm.endDate), new Date())))
                  )}</strong>
                </span>
              </div>
            )}

            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Meta Compartilhada</Label>
                <p className="text-xs text-slate-500">Dividir com outras pessoas</p>
              </div>
              <Switch
                checked={goalForm.isShared}
                onCheckedChange={(checked) => setGoalForm({ ...goalForm, isShared: checked })}
                data-testid="goal-shared-toggle"
              />
            </div>

            {goalForm.isShared && (
              <div className="space-y-2">
                <Label>Participantes</Label>
                <div className="flex flex-wrap gap-2">
                  {participants.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        const isSelected = goalForm.participants.includes(p.id);
                        if (p.id === "me") return; // Can't remove self
                        setGoalForm({
                          ...goalForm,
                          participants: isSelected
                            ? goalForm.participants.filter(id => id !== p.id)
                            : [...goalForm.participants, p.id]
                        });
                      }}
                      className={`px-3 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                        goalForm.participants.includes(p.id)
                          ? "bg-indigo-100 text-indigo-700 border-2 border-indigo-300"
                          : "bg-slate-100 text-slate-600 border-2 border-transparent"
                      }`}
                      data-testid={`participant-${p.id}`}
                    >
                      <span>{p.avatar}</span>
                      <span>{p.name}</span>
                      {goalForm.participants.includes(p.id) && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Button
              onClick={handleSaveGoal}
              disabled={isSaving}
              className="w-full h-12 rounded-xl font-bold bg-indigo-500 hover:bg-indigo-600"
              data-testid="save-goal-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                editingGoal ? "Salvar Alterações" : "Criar Meta"
              )}
            </Button>

          </div>
        </DialogContent>
      </Dialog>

      {/* Goal Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={(open) => { setShowDetailsModal(open); if (!open) setSelectedGoal(null); }}>
        <DialogContent className="w-full max-w-full h-[100dvh] max-h-[100dvh] sm:max-w-md sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-[24px] border-0 sm:border overflow-y-auto p-0 pb-10">
          {selectedGoal && (() => {
            const progress = Math.min(100, Math.round((selectedGoal.currentAmount / selectedGoal.targetAmount) * 100));
            const quote = getMotivationalQuote(progress);
            const isCompleted = progress >= 100;

            return (
              <>
                {/* Header with gradient */}
                <div className={`p-6 ${isCompleted ? "bg-gradient-to-br from-emerald-500 to-emerald-600" : "bg-gradient-to-br from-indigo-500 to-violet-600"}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      {isCompleted 
                        ? <Trophy className="w-7 h-7 text-white" />
                        : <Target className="w-7 h-7 text-white" />
                      }
                    </div>
                    {/* Adicionamos pr-10 para os botões não baterem no "X" de fechar do modal */}
                          <div className="flex gap-2 pr-10"> 
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditGoal(selectedGoal)}
                              className="w-10 h-10 rounded-xl bg-white/20 hover:bg-white/30 text-white"
                              data-testid="edit-goal-btn"
                            >
                              <Pencil className="w-5 h-5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteConfirm(selectedGoal)}
                              className="w-10 h-10 rounded-xl bg-white/20 hover:bg-rose-500 text-white"
                              data-testid="delete-goal-btn"
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </div>
                  </div>
                  <h2 className="text-2xl font-black text-white mb-2">{selectedGoal.title}</h2>
                  <div className="flex items-center gap-2 text-white/80 text-sm mb-4">
                    <Badge className="bg-white/20 text-white border-0">
                      {selectedGoal.isShared ? <><Users className="w-3 h-3 mr-1" /> Compartilhada</> : <><User className="w-3 h-3 mr-1" /> Individual</>}
                    </Badge>
                    {selectedGoal.endDate && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(parseISO(selectedGoal.endDate), "dd/MM/yyyy")}
                      </span>
                    )}
                  </div>
                  
                  {/* Progress */}
                  <div className="bg-white/20 rounded-2xl p-4 backdrop-blur-sm">
                    <div className="flex items-end justify-between mb-2">
                      <span className="text-4xl font-black text-white">{progress}%</span>
                      <span className="text-white/80 text-sm">
                        R$ {formatCurrency(selectedGoal.currentAmount)} / R$ {formatCurrency(selectedGoal.targetAmount)}
                      </span>
                    </div>
                    <Progress value={progress} className="h-3 bg-white/30 [&>div]:bg-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Motivational Quote */}
                  <div className="bg-slate-50 rounded-xl p-4 flex items-start gap-3">
                    <span className="text-2xl">{quote.emoji}</span>
                    <p className="text-slate-700 text-sm font-medium italic">{quote.text}</p>
                  </div>

                  {/* Participants Summary (if shared) */}
                  {selectedGoal.isShared && (
                    <div>
                      <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <Users className="w-4 h-4 text-indigo-500" />
                        Participantes
                      </h3>
                      <div className="space-y-2">
                        {selectedGoal.participants.map(participantId => {
                          const participant = participants.find(p => p.id === participantId);
                          const balance = getParticipantBalance(selectedGoal, participantId);
                          const contribution = getParticipantContribution(selectedGoal, participantId);
                          
                          return (
                            <div key={participantId} className="bg-white rounded-xl p-3 border border-slate-200 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-xl">{participant?.avatar}</span>
                                <span className="font-medium text-slate-900">{participant?.name}</span>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-indigo-600">R$ {formatCurrency(balance)}</p>
                                <p className="text-xs text-slate-500">{contribution}% contribuição</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Movement History */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-slate-900 flex items-center gap-2">
                        <History className="w-4 h-4 text-indigo-500" />
                        Histórico
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenMovementModal(selectedGoal)}
                        className="rounded-xl text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                        data-testid="add-movement-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Lançar
                      </Button>
                    </div>
                    
                    {selectedGoal.movements.length === 0 ? (
                      <p className="text-center text-slate-500 py-6 text-sm">
                        Nenhuma movimentação ainda
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {[...selectedGoal.movements].reverse().map((movement) => {
                          const participant = participants.find(p => p.id === movement.participantId);
                          const isDeposit = movement.type === "deposit";
                          
                          return (
                            <div
                              key={movement.id}
                              onClick={() => handleEditMovement(movement)}
                              className="bg-white rounded-xl p-3 border border-slate-200 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                              data-testid={`movement-${movement.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                  isDeposit ? "bg-emerald-100" : "bg-rose-100"
                                }`}>
                                  {isDeposit 
                                    ? <ArrowUpCircle className="w-5 h-5 text-emerald-600" />
                                    : <ArrowDownCircle className="w-5 h-5 text-rose-600" />
                                  }
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-slate-900">
                                      {isDeposit ? "Aporte" : "Retirada"}
                                    </span>
                                    <span className="text-xs text-slate-500">{participant?.avatar}</span>
                                  </div>
                                  <p className="text-xs text-slate-500">
                                    {format(parseISO(movement.date), "dd/MM/yyyy")}
                                    {movement.note && ` • ${movement.note}`}
                                  </p>
                                </div>
                              </div>
                              <span className={`font-bold ${isDeposit ? "text-emerald-600" : "text-rose-600"}`}>
                                {isDeposit ? "+" : "-"}R$ {formatCurrency(movement.amount)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Add Movement Modal */}
      <Dialog open={showMovementModal} onOpenChange={(open) => { setShowMovementModal(open); if (!open) resetForms(); }}>
        <DialogContent className="w-full max-w-full h-[100dvh] max-h-[100dvh] sm:max-w-md sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-[24px] border-0 sm:border overflow-y-auto p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Nova Movimentação</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Type Toggle */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMovementForm({ ...movementForm, type: "deposit" })}
                className={`p-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                  movementForm.type === "deposit"
                    ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 text-slate-600"
                }`}
                data-testid="movement-type-deposit"
              >
                <ArrowUpCircle className="w-5 h-5" />
                <span className="font-bold">Aporte</span>
              </button>
              <button
                type="button"
                onClick={() => setMovementForm({ ...movementForm, type: "withdrawal" })}
                className={`p-4 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                  movementForm.type === "withdrawal"
                    ? "border-rose-500 bg-rose-50 text-rose-700"
                    : "border-slate-200 text-slate-600"
                }`}
                data-testid="movement-type-withdrawal"
              >
                <ArrowDownCircle className="w-5 h-5" />
                <span className="font-bold">Retirada</span>
              </button>
            </div>

            {/* Withdrawal warning */}
            {movementForm.type === "withdrawal" && selectedGoal && (
              <div className="bg-rose-50 rounded-xl p-3 flex items-start gap-2 text-sm">
                <AlertTriangle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                <span className="text-rose-700">
                  Só é possível retirar o valor que o participante tem de saldo nesta meta.
                </span>
              </div>
            )}

            {/* Amount */}
            <div className="space-y-2">
              <Label>Valor *</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">R$</span>
                <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="0,00"
                    value={movementForm.amount ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(movementForm.amount) : ""}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "") / 100;
                      setMovementForm({ ...movementForm, amount: val.toString() });
                    }}
                    className="rounded-xl h-12 pl-12 font-bold text-lg"
                    data-testid="movement-amount"
                  />
              </div>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={movementForm.date}
                onChange={(e) => setMovementForm({ ...movementForm, date: e.target.value })}
                className="rounded-xl h-12"
                data-testid="movement-date"
              />
            </div>

            {/* Participant (if shared) */}
            {selectedGoal?.isShared && (
              <div className="space-y-2">
                <Label>Participante</Label>
                <Select
                  value={movementForm.participantId}
                  onValueChange={(value) => setMovementForm({ ...movementForm, participantId: value })}
                >
                  <SelectTrigger className="rounded-xl h-12" data-testid="movement-participant">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedGoal.participants.map(pId => {
                      const p = participants.find(x => x.id === pId);
                      const balance = getParticipantBalance(selectedGoal, pId);
                      return (
                        <SelectItem key={pId} value={pId}>
                          <div className="flex items-center gap-2">
                            <span>{p?.avatar}</span>
                            <span>{p?.name}</span>
                            {movementForm.type === "withdrawal" && (
                              <span className="text-xs text-slate-500">(Saldo: R$ {formatCurrency(balance)})</span>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Note */}
            <div className="space-y-2">
              <Label>Observação</Label>
              <Textarea
                placeholder="Ex: Bônus do trabalho, Emergência..."
                value={movementForm.note}
                onChange={(e) => setMovementForm({ ...movementForm, note: e.target.value })}
                className="rounded-xl resize-none"
                rows={2}
                data-testid="movement-note"
              />
            </div>

            <Button
              onClick={handleSaveMovement}
              disabled={isSaving}
              className={`w-full h-12 rounded-xl font-bold ${
                movementForm.type === "deposit"
                  ? "bg-emerald-500 hover:bg-emerald-600"
                  : "bg-rose-500 hover:bg-rose-600"
              }`}
              data-testid="save-movement-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processando...
                </>
              ) : (
                movementForm.type === "deposit" ? "Registrar Aporte" : "Registrar Retirada"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Movement Modal */}
      <Dialog open={showEditMovementModal} onOpenChange={(open) => { setShowEditMovementModal(open); if (!open) resetForms(); }}>
        <DialogContent className="w-full max-w-full h-[100dvh] max-h-[100dvh] sm:max-w-md sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-[24px] border-0 sm:border overflow-y-auto p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Editar Movimentação</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Valor *</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">R$</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={movementForm.amount ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(parseFloat(movementForm.amount)) : ""}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "") / 100;
                    setMovementForm({ ...movementForm, amount: val.toString() });
                  }}
                  className="rounded-xl h-12 pl-12 font-bold text-lg"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={movementForm.date}
                onChange={(e) => setMovementForm({ ...movementForm, date: e.target.value })}
                className="rounded-xl h-12"
              />
            </div>

            <div className="space-y-2">
              <Label>Observação</Label>
              <Textarea
                placeholder="Observação..."
                value={movementForm.note}
                onChange={(e) => setMovementForm({ ...movementForm, note: e.target.value })}
                className="rounded-xl resize-none"
                rows={2}
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteMovementConfirm(editingMovement)}
                className="flex-1 h-12 rounded-xl font-bold text-rose-600 border-rose-200 hover:bg-rose-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </Button>
              <Button
                onClick={handleUpdateMovement}
                disabled={isSaving}
                className="flex-1 h-12 rounded-xl font-bold bg-indigo-500 hover:bg-indigo-600"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  "Salvar"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Goal Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent className="rounded-[24px] mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir meta?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que quer excluir "{deleteConfirm?.title}"? Todo o histórico de movimentações será perdido. Essa ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGoal}
              className="rounded-xl bg-rose-500 hover:bg-rose-600"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Movement Confirmation */}
      <AlertDialog open={!!deleteMovementConfirm} onOpenChange={(open) => !open && setDeleteMovementConfirm(null)}>
        <AlertDialogContent className="rounded-[24px] mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir movimentação?</AlertDialogTitle>
            <AlertDialogDescription>
              O saldo e progresso da meta serão recalculados automaticamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" onClick={() => setShowEditMovementModal(false)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { handleDeleteMovement(); setShowEditMovementModal(false); }}
              className="rounded-xl bg-rose-500 hover:bg-rose-600"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
